import { faCalendarClock, faEyeSlash, faPlane, faStore, faThumbTack } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { ReactNode, useMemo } from 'react';
import { Image, Pressable, View } from 'react-native';
import useStyles from '../../../hooks/styles-hook';
import { MissionFilter } from '../../../models/MapModels';
import { InfoBoxProps } from '../InfoBox';
import Label, { LabelType } from '../Label';
import MissionCardLoading from './MissionCardLoading';
import pinSmallOrange from '../../../../assets/pin-small-orange.png';
import useCustomTheme, { CustomTheme, lightTheme } from '../../../hooks/theme-hook';
import { GeoLocation, GetAirportMissionZoneDto, MissionBranchAirportInfoDto, MissionType } from '../../../api/api.g';
import type { MissionDiscoveryAvailabilityStatus } from '../../../api/api.g';
import moment from 'moment';
import useTranslation from '../../../hooks/translation-hook';
import boltIcon from '../../../../assets/bolt-icon.png';
import boltIconBw from '../../../../assets/bolt-icon-bw.png';
import { isMissionAvailableForDiscovery } from '../../../models/helpers/missionAvailabilityHelper';

export interface MissionDetailCardProps {
  branchId?: string;
  prof?: string;
  title?: string;
  purchaseRequired?: boolean;
  points?: number;
  tag?: string;
  startTime?: string;
  endTime?: string;
  selected?: boolean;
  // onPress(e: MissionDetailCardProps): void;
}

export interface AirportMissionSearchContext {
  searchDateTime?: string;
  onlyAcceptableMissions?: boolean;
}

export interface MissionCardProps extends InfoBoxProps {
  id: string;
  geocoordinate: GeoLocation;
  address?: string;
  logoUrl?: string;
  industry: string;
  distance?: string;
  missionDetailCardProps: MissionDetailCardProps[];
  drawerVersion?: boolean;
  showAsCards?: boolean;
  selectedMission?: MissionFilter;
  loading?: boolean;
  pinned?: boolean;
  isHidden?: boolean;
  isFlashMission?: boolean;
  missionType?: MissionType;
  airportMissionCount?: number;
  airportRouteOptions?: GetAirportMissionZoneDto[];
  airportSearchContext?: AirportMissionSearchContext;
  branchAirportInfo?: MissionBranchAirportInfoDto;
  canAccept?: boolean;
  isAvailableForDiscovery?: boolean;
  discoveryAvailabilityStatus?: MissionDiscoveryAvailabilityStatus;
  upcomingPeriod?: boolean;
  upcomingPeriodStartDate?: Date;
  onPress?(missionDetails: MissionCardProps): void;
}

const MissionCard = React.memo((props: MissionCardProps) => {
  const t = useTranslation();
  const theme: CustomTheme = useCustomTheme();
  const styles = useStyles((section) => section.missionCard);
  const stylesDetail = useStyles((sector) => sector.missionDetailCard);
  const isFlashCard = !!props.isFlashMission;
  const isDarkMode = !!theme.isDark;
  const flashTitleColor = isDarkMode ? '#FFFFFF' : lightTheme.text?.titleDark?.toString() ?? '#025164';
  const flashBodyColor = isDarkMode ? '#D8E2EA' : lightTheme.text?.primary?.toString() ?? 'black';
  const flashTagColor = isDarkMode ? '#74CCBE' : flashBodyColor;

  const selected = !!props.selectedMission && props.id === props.selectedMission.branchId;
  const useFixedListHeight = !props.showAsCards && !props.upcomingPeriod;
  const isAvailableForDiscovery = isMissionAvailableForDiscovery(props);

  const logo = useMemo<ReactNode>(() => {
    if (props.logoUrl) {
      return <Image style={styles.header.logo.image} source={{ uri: props.logoUrl }} />;
    }

    return <FontAwesomeIcon icon={faStore} style={styles.header.logo.icon} />;
  }, [props.logoUrl, styles.header.logo]);

  const address = props.address ? (
    <Label variant={LabelType.body1} style={isFlashCard ? { color: flashBodyColor } : undefined}>
      {props.address}
    </Label>
  ) : null;

  return (
    <Pressable
      {...props}
      onPress={(e) => {
        // console.log('Mission clicked', props.id);

        if (props.onPress) {
          props.onPress(props);
        }
      }}
      style={[
        styles.containerBase,
        useFixedListHeight ? styles.container.listItem : {},
        props.drawerVersion ? styles.container.drawer : {},
        props.showAsCards ? styles.container.card : {},
        selected ? styles.container.selected : {},
        props.pinned ? styles.container.pinned : {},
        props.isFlashMission ? styles.container.flashColor : {},
      ]}
    >
      {props.loading && <MissionCardLoading />}
      <View style={[styles.cardBase, { marginBottom: 8, opacity: isAvailableForDiscovery ? 1 : 0.4 }]}>
        <View style={styles.header.logoBase}>{logo}</View>
        <View style={styles.info}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <View style={{ flexGrow: 1, flexShrink: 1 }}>
              <View style={styles.missionName}>
                <Label variant={LabelType.title} style={isFlashCard ? { color: flashTitleColor } : undefined}>
                  {`${props.title}`}
                </Label>
                <View style={[styles.missionIcons, { flexGrow: props.isFlashMission ? 1 : 0 }]}>
                  {props.isHidden ? (
                    <FontAwesomeIcon icon={faEyeSlash} style={styles.missionHiddenIcon} color={isFlashCard ? flashBodyColor : undefined} />
                  ) : (
                    <Label></Label>
                  )}
                  {props.missionType === MissionType.Airport && (
                    <View>
                      <FontAwesomeIcon icon={faPlane} style={styles.missionHiddenIcon} color={isFlashCard ? flashBodyColor : undefined} />
                    </View>
                  )}
                  {props.isFlashMission && (
                    <View>
                      {isAvailableForDiscovery ? (
                        <Image source={boltIcon} style={styles.flashMissionIcon} />
                      ) : (
                        <Image source={boltIconBw} style={styles.flashMissionIcon} />
                      )}
                    </View>
                  )}
                </View>
              </View>
              {address}
            </View>
            {props.pinned && <FontAwesomeIcon size={18} color={theme.text.darkTurquoiseTwo.toString()} icon={faThumbTack} />}
          </View>
          <View style={styles.preTitle}>
            <Label variant={LabelType.caption} style={isFlashCard ? { color: flashTagColor } : undefined}>
              {props.industry.length === 0 ? 'N/A' : props.industry}
            </Label>
            <Label style={{ flexShrink: 0, ...(isFlashCard ? { color: flashBodyColor } : {}) }} variant={LabelType.caption}>
              {props.distance}
            </Label>
          </View>
        </View>
      </View>
      {props.upcomingPeriod && (
        <View key={`upcomingMissionInfo_${props.id}`} style={stylesDetail.missionsBranchDetail.content.upcoming.container}>
          <View style={stylesDetail.missionsBranchDetail.content.upcoming.date.container}>
            <FontAwesomeIcon
              icon={faCalendarClock}
              style={stylesDetail.missionsBranchDetail.content.upcoming.date.icon}
              color={theme.indicatorColor.toString()}
            />
            <Label variant={LabelType.caption} style={{ color: theme.indicatorColor }}>
              {t('starting-on')}
            </Label>
          </View>
          <Label variant={LabelType.caption} style={{ color: theme.indicatorColor, marginLeft: 4 }}>
            {moment(props.upcomingPeriodStartDate).format('YYYY-MM-DD')}
          </Label>
        </View>
      )}
      {props.missionDetailCardProps &&
        false &&
        props.missionDetailCardProps.map((item, index) => {
          return (
            <View key={`missionDetail${index}`} style={[stylesDetail.missionsBranchDetail.content.infoContainerBase, { marginBottom: 4 }]}>
              <View style={stylesDetail.missionsBranchDetail.content.infoContainer.title}>
                <Image source={pinSmallOrange} style={stylesDetail.missionsBranchDetail.content.infoContainer.icon} />
                <Label variant={LabelType.titleSmall}>{item.title}</Label>
              </View>
            </View>
          );
        })}
    </Pressable>
  );
});

export default MissionCard;
